import { action } from '@ember/object';
import { tracked } from '@glimmer/tracking';

import type { PopupData } from 'webapp-types';

// Class is not abstract because of problems with loading template
import CommonUiIconComponent from 'webapp/components/common/ui-icon';
import type { OptionRaw } from 'webapp/components/form/select';
import FormSelectBoardComponent from 'webapp/components/form/select-board';
import type { ActionOption } from 'webapp/components/form/select-with-action';
import PopupInnerModalComponent from 'webapp/components/popup/inner/modal';
import SubmitButtonComponent from 'webapp/components/submit-button';

import type Board from 'webapp/models-custom/board';

export default class BoardsPopupBoardToObjectBaseComponent extends PopupInnerModalComponent<PopupData.Board.ToObject> {
	@tracked selectedId = '';

	popupTargetObject = '';
	popupIcon!: IconName;
	selectName = '';
	selectPlaceholder = '';

	get options(): OptionRaw<string>[] {
		return []; // extended
	}

	get actionOptions(): ActionOption[] {
		return []; // extended
	}

	get saveTooltipText() {
		return ''; // extended
	}

	get model(): Board | Board[] | null {
		return this.popupData.model || null;
	}

	get popupTitle(): string {
		const model = this.model,
			text = Array.isArray(model) ? `s (${model.length})` : ` "${model?.name}"`;
		return `Add dashboard${text} to ${this.popupTargetObject}`;
	}

	get isSaveDisabled(): boolean {
		return false;
	}

	getStatusIcon(isEditable: boolean, isFavorited: boolean, isAlreadyUsed?: boolean): string {
		let statusText = '',
			disabledText = '';

		if (isFavorited) {
			statusText += '<ui-icon name="StarFill" size="sm"></ui-icon>';
		}

		if (isAlreadyUsed) {
			const model = this.model,
				isArray = Array.isArray(model),
				text = isArray ? `s (${model.length}) are` : ` "${model?.name}" is already`;
			disabledText = `${isArray ? 'All of the dashboard' : 'Dashboard'}${text} used in this ${this.popupTargetObject.toLowerCase()}.`;
		}

		if (!isEditable) {
			disabledText = `You don’t have permissions to edit this ${this.popupTargetObject.toLowerCase()}.`;
		}

		if (isAlreadyUsed || !isEditable) {
			statusText += `<ui-icon name="InfoFill" size="sm" title="${disabledText}"></ui-icon>`;
		}

		return statusText;
	}

	@action
	changeSelected(selectedId: string): void {
		this.selectedId = selectedId;
	}

	@action
	save(): void {
		if (!this.selectedId) {
			return;
		}

		if (this.submitInProgress) {
			return;
		}

		const onSubmit = this.popupData.onSubmit;
		if (onSubmit) {
			this.submitInProgress = true;

			void onSubmit(this.selectedId)
				.then(() => {
					this.close();
				})
				.catch()
				.finally(() => {
					this.submitInProgress = false;
				});
		}
	}
	<template>
		<div class="title">
			<CommonUiIconComponent @name={{this.popupIcon}} @size="lg" />
			{{this.popupTitle}}
		</div>
		<div class="content">
			<div class="row">
				<FormSelectBoardComponent
					name={{this.selectName}}
					@selected={{this.selectedId}}
					@options={{this.options}}
					@actionOptions={{this.actionOptions}}
					@onChange={{this.changeSelected}}
					@placeholder={{this.selectPlaceholder}}
					@isNumeric={{false}}
					@isRequired={{true}}
					@extraClass="cs-image avatar-wrapper-borders"
					@dropdownSearchPlaceholder="Search"
					@showFloatlabel={{false}}
					@tooltipOnSelectionFromProperty=""
				/>
			</div>
			<div class="controls2">
				<div class="controls2__right">
					<SubmitButtonComponent
						@text="Cancel"
						@theme="grey-empty"
						@noDelay={{true}}
						@onTrigger={{this.close}}
					/>
					<SubmitButtonComponent
						@text="Save"
						@extraClass="save"
						@onTrigger={{this.save}}
						@submitInProgress={{this.submitInProgress}}
						@isDisabled={{this.isSaveDisabled}}
						@tooltipText={{this.saveTooltipText}}
					/>
				</div>
			</div>
		</div>
	</template>
}
